import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { InjectionToken } from '@angular/core';

/**
 * - `offline`: the browser has no connection.
 * - `endpoint_blocked`: backend answers a health probe, but this request died
 *   (proxy/DLP/WAF/size limit) — a local problem, the backend itself is fine.
 * - `backend_unreachable`: backend does not answer at all.
 */
export type NetworkFailureKind =
  | 'offline'
  | 'endpoint_blocked'
  | 'backend_unreachable';

/**
 * Notified by `ErrorHandlerInterceptor` when a request to our own backend
 * failed at the network layer (see `isNetworkFailureStatus`). The app decides
 * what to do with it (e.g. report to RUM); the default implementation is a no-op.
 */
export interface NetworkFailureReporter {
  report(
    request: HttpRequest<unknown>,
    response: HttpErrorResponse,
    kind: NetworkFailureKind
  ): void;
}

export const NETWORK_FAILURE_REPORTER =
  new InjectionToken<NetworkFailureReporter>('NETWORK_FAILURE_REPORTER', {
    providedIn: 'root',
    factory: () => ({ report: () => undefined }),
  });

const failureKinds = new WeakMap<HttpErrorResponse, NetworkFailureKind>();

/** Attaches the classified kind to an error so callers further down the stream can read it. */
export function tagNetworkFailure(
  error: HttpErrorResponse,
  kind: NetworkFailureKind
): void {
  failureKinds.set(error, kind);
}

export function networkFailureKindOf(
  error: unknown
): NetworkFailureKind | undefined {
  return error instanceof HttpErrorResponse
    ? failureKinds.get(error)
    : undefined;
}
